/**
 * US State bounding-box lookup and geolocation hook.
 * No external API required — pure coordinate math.
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';

// [minLat, maxLat, minLon, maxLon]
const STATE_BOUNDS = {
  AK: [51.2,  71.5, -180.0, -130.0],
  AL: [30.1,  35.0,  -88.5,  -84.9],
  AR: [33.0,  36.5,  -94.6,  -89.6],
  AZ: [31.3,  37.0, -114.8, -109.0],
  CA: [32.5,  42.0, -124.4, -114.1],
  CO: [37.0,  41.0, -109.0, -102.0],
  CT: [41.0,  42.1,  -73.7,  -71.8],
  DC: [38.80, 38.99, -77.1,  -76.9],
  DE: [38.4,  39.8,  -75.8,  -75.0],
  FL: [24.4,  31.0,  -87.6,  -80.0],
  GA: [30.4,  35.0,  -85.6,  -80.8],
  HI: [18.9,  22.2, -160.2, -154.8],
  IA: [40.4,  43.5,  -96.6,  -90.1],
  ID: [41.9,  49.0, -117.2, -111.0],
  IL: [36.9,  42.5,  -91.5,  -87.5],
  IN: [37.8,  41.8,  -88.1,  -84.8],
  KS: [37.0,  40.0, -102.1,  -94.6],
  KY: [36.5,  39.1,  -89.6,  -82.6],
  LA: [28.9,  33.0,  -94.0,  -89.0],
  MA: [41.2,  42.9,  -73.5,  -69.9],
  MD: [37.9,  39.7,  -79.5,  -75.0],
  ME: [43.1,  47.5,  -71.1,  -66.9],
  MI: [41.7,  47.5,  -90.4,  -82.4],
  MN: [43.5,  49.4,  -97.2,  -89.5],
  MO: [36.0,  40.6,  -95.8,  -89.1],
  MS: [30.2,  35.0,  -91.7,  -88.1],
  MT: [44.4,  49.0, -116.0, -104.0],
  NC: [33.8,  36.6,  -84.3,  -75.5],
  ND: [45.9,  49.0, -104.1,  -96.6],
  NE: [40.0,  43.0, -104.1,  -95.3],
  NH: [42.7,  45.3,  -72.6,  -70.6],
  NJ: [38.9,  41.4,  -75.6,  -73.9],
  NM: [31.3,  37.0, -109.1, -103.0],
  NV: [35.0,  42.0, -120.0, -114.0],
  NY: [40.5,  45.0,  -79.8,  -71.9],
  OH: [38.4,  42.3,  -84.8,  -80.5],
  OK: [33.6,  37.0, -103.0,  -94.4],
  OR: [42.0,  46.3, -124.6, -116.5],
  PA: [39.7,  42.3,  -80.5,  -74.7],
  RI: [41.1,  42.0,  -71.9,  -71.1],
  SC: [32.0,  35.2,  -83.4,  -78.5],
  SD: [42.5,  45.9, -104.1,  -96.4],
  TN: [34.9,  36.7,  -90.3,  -81.6],
  TX: [25.8,  36.5, -106.6,  -93.5],
  UT: [37.0,  42.0, -114.1, -109.0],
  VA: [36.5,  39.5,  -83.7,  -75.2],
  VT: [42.7,  45.0,  -73.4,  -71.5],
  WA: [45.5,  49.0, -124.7, -116.9],
  WI: [42.5,  47.1,  -92.9,  -86.8],
  WV: [37.2,  40.6,  -82.6,  -77.7],
  WY: [41.0,  45.0, -111.1, -104.1],
};

/**
 * Returns the US state abbreviation for a lat/lon pair, or null if outside all known bounds.
 * When coordinates fall in multiple overlapping boxes, returns the one whose centroid is closest.
 */
export function coordsToState(lat, lon) {
  const matches = Object.entries(STATE_BOUNDS).filter(
    ([, [minLat, maxLat, minLon, maxLon]]) =>
      lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon,
  );
  if (!matches.length) return null;
  if (matches.length === 1) return matches[0][0];
  // Pick closest centroid
  return matches.sort(([, [a, b, c, d]], [, [e, f, g, h]]) => {
    const dA = Math.abs(lat - (a + b) / 2) + Math.abs(lon - (c + d) / 2);
    const dB = Math.abs(lat - (e + f) / 2) + Math.abs(lon - (g + h) / 2);
    return dA - dB;
  })[0][0];
}

/**
 * React hook that fires browser geolocation, resolves to a US state abbreviation,
 * and calls onState(state) on success or shows a toast on failure.
 */
export function useGeoState(onState) {
  const [loading, setLoading] = useState(false);

  const detect = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser.');
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setLoading(false);
        const state = coordsToState(coords.latitude, coords.longitude);
        if (state) {
          onState(state);
        } else {
          toast.error('Location access denied. Please select your state manually.');
        }
      },
      () => {
        setLoading(false);
        toast.error('Location access denied. Please select your state manually.');
      },
      { timeout: 10000, maximumAge: 300000 },
    );
  }, [onState]);

  return { loading, detect };
}
